import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    LicensePricingType,
    PlatformLicensePriceIntervalPerUserDTO,
    PlatformLicensePricingDTO
} from '@iota/iota-management-portal-v4-client';
import { KeysOfType } from '../../constants-and-enums/helper-types';
import { PlatformLicenseEntity, PlatformLicensePricingEntity } from 'src/app/store/entity-models';
import { Subscription } from 'rxjs';
import { getNumberFormattedWithDecimalPlaces } from '../../utils';

// Type-safe form with values from PlatformLicensePricingDTO and JSON for the price intervals
type PlatformLicensePricingControls = Partial<Record<keyof PlatformLicensePricingDTO, FormControl | FormArray>>;

type PlatformLicensePriceIntervalControls = Record<keyof PlatformLicensePriceIntervalPerUserDTO, FormControl>;

const LICENSE_PROPERTIES_TO_FORMAT_WITH_AT_LEAST_TWO_DECIMAL_PLACES: KeysOfType<PlatformLicensePricingDTO, number>[] = [
    'licensePriceMonthly',
    'licensePriceYearly',
    'licenseSalesTax'
];
const LICENSE_INTERVAL_PROPERTIES_TO_FORMAT_WITH_AT_LEAST_TWO_DECIMAL_PLACES: KeysOfType<PlatformLicensePriceIntervalPerUserDTO, number>[] =
    ['priceMonthly', 'priceYearly'];

@Component({
    standalone: false,
    selector: 'app-platform-license-pricing-form',
    templateUrl: './platform-license-pricing-form.component.html',
    styleUrls: ['./platform-license-pricing-form.component.scss']
})
export class PlatformLicensePricingFormComponent implements OnInit, OnChanges, OnDestroy {
    @Input() platformLicense: PlatformLicenseEntity;
    @Input() platformLicensePricing: PlatformLicensePricingEntity;
    @Input() editMode = false;
    @Input() disableEffectiveDates = false;

    @Output() formValid = new EventEmitter<boolean>();

    formValidSubscription: Subscription;

    platformLicensePricingForm = this.fb.group<PlatformLicensePricingControls>(
        {
            internalDescription: new FormControl(''),
            licensePriceMonthly: new FormControl<number>(null, { validators: [Validators.required] }),
            licensePriceYearly: new FormControl<number>(null, { validators: [Validators.required] }),
            licenseSalesTax: new FormControl<number>(null, { validators: [Validators.required, Validators.min(0), Validators.max(1)] }),
            priceIntervalsPerUser: this.fb.array([]),
            effectiveFrom: new FormControl<Date>(null),
            effectiveTo: new FormControl<Date>(null)
        },
        {
            validators: [this.checkPriceIntervalsValidity]
        }
    );

    platformLicenseCompanyUrl: string;

    constructor(private fb: FormBuilder) {}

    ngOnInit(): void {
        this.formValidSubscription = this.platformLicensePricingForm.valueChanges.subscribe(() => {
            this.formValid.emit(this.platformLicensePricingForm.valid);
        });

        this.enableOrDisableFieldsDependingOnPricingType();
        this.initForm(this.editMode);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const previousPlatformLicense = changes?.platformLicense?.previousValue;
        const currentPlatformLicense = changes?.platformLicense?.currentValue;

        const previousPlatformLicensePricing = changes?.platformLicensePricing?.previousValue;
        const currentPlatformLicensePricing = changes?.platformLicensePricing?.currentValue;

        const previousEditMode = changes?.editMode?.previousValue;
        const currentEditMode = changes?.editMode?.currentValue;

        if (
            previousPlatformLicense !== currentPlatformLicense ||
            previousPlatformLicensePricing !== currentPlatformLicensePricing ||
            previousEditMode !== currentEditMode
        ) {
            this.initForm(this.editMode);
        }
    }

    ngOnDestroy(): void {
        this.formValidSubscription?.unsubscribe();
    }

    async initForm(enableEditing = false) {
        if (this.platformLicensePricing) {
            this.platformLicensePricingForm.patchValue({
                ...this.platformLicensePricing
            });

            if (this.platformLicensePricing?.priceIntervalsPerUser) {
                this.priceIntervalsPerUser.clear();
                this.platformLicensePricing?.priceIntervalsPerUser?.forEach((priceInterval) =>
                    this.addPriceInterval(priceInterval, enableEditing)
                );
            }
        }

        if (enableEditing) {
            this.editMode = true;
            this.platformLicensePricingForm.enable();
            this.enableOrDisableFieldsDependingOnPricingType();

            if (this.disableEffectiveDates) {
                this.platformLicensePricingForm.controls.effectiveFrom.disable();
                this.platformLicensePricingForm.controls.effectiveTo.disable();
            }
        } else {
            this.editMode = false;
            this.platformLicensePricingForm.disable();

            // Numbers are formatted as long as the form is disabled
            for (const property of LICENSE_PROPERTIES_TO_FORMAT_WITH_AT_LEAST_TWO_DECIMAL_PLACES) {
                this.platformLicensePricingForm
                    .get(property)
                    ?.setValue(getNumberFormattedWithDecimalPlaces(this.platformLicensePricing?.[property]));
            }
        }
    }

    enableEditing() {
        this.initForm(true);
    }

    disableEditingAndDontInitializeForm() {
        this.editMode = false;
        this.platformLicensePricingForm.disable();
    }

    enableOrDisableFieldsDependingOnPricingType() {
        const pricingType = this.platformLicense?.licensePricingType;
        if (!pricingType) return;

        if (this.shouldPriceMonthlyAndYearlyBeVisible) {
            this.platformLicensePricingForm.controls.licensePriceMonthly.enable();
            this.platformLicensePricingForm.controls.licensePriceYearly.enable();
        } else {
            this.platformLicensePricingForm.controls.licensePriceMonthly.disable();
            this.platformLicensePricingForm.controls.licensePriceYearly.disable();
        }

        if (this.shouldPriceIntervalsBeVisible) {
            this.priceIntervalsPerUser.enable();
        } else {
            this.priceIntervalsPerUser.disable();
        }
    }

    get shouldPriceMonthlyAndYearlyBeVisible() {
        return (
            this.platformLicense?.licensePricingType === LicensePricingType.SINGLE_USER_LICENSE ||
            this.platformLicense?.licensePricingType === LicensePricingType.VOLUME_LICENSE
        );
    }

    get shouldPriceIntervalsBeVisible() {
        return this.platformLicense?.licensePricingType === LicensePricingType.SCALE_PRICE_LICENSE;
    }

    get priceIntervalsPerUser() {
        return this.platformLicensePricingForm?.controls?.priceIntervalsPerUser as FormArray;
    }

    addPriceInterval(priceInterval: PlatformLicensePriceIntervalPerUserDTO, enableEditing = false) {
        // Get the highest value for the end of the price intervals
        const priceIntervalValues = this.priceIntervalsPerUser?.controls?.map((c) => c.value) as PlatformLicensePriceIntervalPerUserDTO[];
        const highestPriceIntervalEnd = Math.max(...priceIntervalValues?.map((priceInterval) => priceInterval.maxNumberOfEmployees), 0);
        const nextPriceIntervalBegin = isNaN(highestPriceIntervalEnd) || highestPriceIntervalEnd === 0 ? 0 : highestPriceIntervalEnd + 1;

        const priceIntervalForm = this.fb.group<PlatformLicensePriceIntervalControls>(
            {
                minNumberOfEmployees: new FormControl<number>(priceInterval?.minNumberOfEmployees ?? nextPriceIntervalBegin, {
                    validators: [Validators.required]
                }),
                maxNumberOfEmployees: new FormControl<number>(priceInterval?.maxNumberOfEmployees, { validators: [Validators.required] }),
                priceMonthly: new FormControl<number>(priceInterval?.priceMonthly, { validators: [Validators.required] }),
                priceYearly: new FormControl<number>(priceInterval?.priceYearly, { validators: [Validators.required] })
            },
            { validators: [this.checkPriceIntervalBeginIsSmallerThanEnd] }
        );
        // Numbers are formatted as long as the form is disabled
        if (!enableEditing) {
            for (const property of LICENSE_INTERVAL_PROPERTIES_TO_FORMAT_WITH_AT_LEAST_TWO_DECIMAL_PLACES) {
                priceIntervalForm.get(property)?.setValue(getNumberFormattedWithDecimalPlaces(priceInterval?.[property]));
            }
        }

        this.priceIntervalsPerUser.push(priceIntervalForm);
    }

    removePriceInterval(index: number) {
        this.priceIntervalsPerUser.removeAt(index);
    }

    checkPriceIntervalBeginIsSmallerThanEnd(group: FormGroup<PlatformLicensePriceIntervalControls>) {
        const minNumberOfEmployees = group?.value?.minNumberOfEmployees;
        const maxNumberOfEmployees = group?.value?.maxNumberOfEmployees;
        if (minNumberOfEmployees >= maxNumberOfEmployees) {
            return { beginGreaterThanEnd: true };
        }

        return null;
    }

    checkPriceIntervalsValidity(group: FormGroup<PlatformLicensePricingControls>) {
        const allPriceIntervals = group?.value?.priceIntervalsPerUser as PlatformLicensePriceIntervalPerUserDTO[];
        const priceIntervalsIntersect = allPriceIntervals?.some((priceInterval, index) => {
            const intersectingPriceIntervals = allPriceIntervals?.filter((otherPriceInterval, otherIndex) => {
                return (
                    index !== otherIndex &&
                    priceInterval.minNumberOfEmployees <= otherPriceInterval.maxNumberOfEmployees &&
                    priceInterval.maxNumberOfEmployees >= otherPriceInterval.minNumberOfEmployees
                );
            });
            return intersectingPriceIntervals?.length > 0;
        });
        if (priceIntervalsIntersect) {
            return { priceIntervalsIntersect: true };
        }

        // Check if there are gaps in price intervals (maxNumberOfEmployees+1 must always be minNumberOfEmployees of another one)
        const priceIntervalsWithGaps = allPriceIntervals?.filter((priceInterval) => {
            const biggerPriceIntervals = allPriceIntervals?.filter(
                (otherPriceInterval) => otherPriceInterval?.minNumberOfEmployees > priceInterval?.maxNumberOfEmployees
            );
            if (biggerPriceIntervals?.length === 0) {
                return false;
            }

            return !biggerPriceIntervals?.some(
                (otherPriceInterval) => priceInterval?.maxNumberOfEmployees + 1 === otherPriceInterval?.minNumberOfEmployees
            );
        });
        if (priceIntervalsWithGaps?.length > 0) {
            return { priceIntervalsHaveGaps: true };
        }

        return null;
    }

    getUpsertedPlatformLicensePricing(): PlatformLicensePricingDTO {
        if (!this.platformLicensePricingForm.valid) return null;

        const existingPlatformLicensePricing = !!this.platformLicensePricing ? this.platformLicensePricing : {};
        const updatedPlatformLicensePricing: PlatformLicensePricingDTO = {
            platformLicenseId: this.platformLicense?.platformLicenseId,
            ...existingPlatformLicensePricing,
            ...this.platformLicensePricingForm.getRawValue()
        };

        // Sort the price intervals
        if (updatedPlatformLicensePricing?.priceIntervalsPerUser) {
            updatedPlatformLicensePricing.priceIntervalsPerUser = updatedPlatformLicensePricing.priceIntervalsPerUser.sort(
                (a, b) => a.minNumberOfEmployees - b.minNumberOfEmployees
            );
        }

        return updatedPlatformLicensePricing;
    }
}
